import { useState, useContext } from 'react'
import styled from 'styled-components'
import { Input } from '@giphy/react-giphy-brand'
import { Button } from 'shared/components/buttons'
import Tooltip from 'shared/components/ui/tooltip'
import { giphyRed, giphyWhite, giphyBlue } from '@giphy/js-brand'
import Checkbox from 'shared/components/checkbox'
import { without, concat } from 'lodash'
import { reportSearch } from 'shared/api/index'
import MessageContext from 'shared/contexts/message'

const SEARCH_INFO = 360019823451
const REPORT_GIF = 360020287371
const GUIDELINES = 360020286811
const DMCA = 360020287091

const REASONS_ORDER = [
    'NOT_RELEVANT',
    'NOT_FUN',
    'NOT_SAFE',
    'NOT_TIMELY',
    'NOT_ENOUGH_VERIFIED_CONTENT',
    'NOT_DIVERSE',
]

const REASONS_DESCRIPTION = {
    NOT_RELEVANT: 'Results are not relevant',
    NOT_FUN: 'Results are not very fun',
    NOT_SAFE: 'Results are unsafe',
    NOT_TIMELY: 'Results do not have the newest content',
    NOT_ENOUGH_VERIFIED_CONTENT: 'Results aren’t high quality',
    NOT_DIVERSE: 'Results are not diverse or lack representation',
}

const Textarea = styled(Input)`
    margin: 0;
    height: 100px;
    textarea {
        width: 100%;
        margin: 0;
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        color: #000;
        padding: 10px;
        outline: none;
    }
`

const Modal = styled.div`
    position: fixed;
    width: 546px;
    height: 667px;
    background-color: #212121;
    padding: 45px 35px 35px 35px;
    z-index: 9999999;
    left: calc(50% - 273px);
    top: calc(50% - 337px);
    -webkit-font-smoothing: antialiased;
    p {
        font-size: 14px;
        line-height: 18px;
        margin-top: 20px;
    }
    a {
        font-weight: bold;
        color: ${giphyBlue};
        &:hover {
            text-decoration: underline;
        }
    }
`

const StyledCheckbox = styled(Checkbox)`
    margin-right: 5px;
`

const CheckboxContainer = styled.label<{ stacked: boolean }>`
    display: ${(props) => (props.stacked ? 'block' : 'inline-block')};
    padding: ${(props) => (props.stacked ? '3px 0' : '0 35px 0 0')};
    color: rgb(255, 255, 255);
    font-size: 14px;
    font-weight: bold;
`

const Heading = styled.div`
    color: rgb(207, 207, 207);
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 15px;
`

const Term = styled.span`
    color: ${giphyRed};
`

const ReasonsList = styled.div`
    margin: 0px 0 20px;
`

const AdditionalInfoInput = styled(Textarea)`
    margin-bottom: 25px;
    height: 68px;
`

const Actions = styled.div`
    margin-top: 20px;
`

const SubmitButton = styled(Button)`
    display: block;
    width: 100%;
`

const CancelButton = styled(Button)`
    display: block;
    width: 100%;
    background-color: #212121;
`

const ColorBar = styled.div`
    background: linear-gradient(63.434948822922024deg, #06ff99 0%, #6157ff 100%);
    height: 5px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
`

const CloseButton = styled.div`
    mask-image: url(/static/img/close.svg);
    background-color: white;
    position: absolute;
    right: 20px;
    top: 20px;
    width: 30px;
    height: 30px;
    cursor: pointer;
`

const Dim = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: black;
    opacity: 0.8;
    z-index: 9999;
`

const ContentType = styled.div`
    margin: 0 0 20px;
`

const RequiredTooltip = styled(Tooltip)`
    color: white;
    display: inline-block;
    padding-left: 13px;
`

const RequiredLabel = styled.div`
    height: 17px;
    color: rgb(82, 82, 82);
    font-size: 14px;
    font-weight: bold;
    line-height: 17px;
`

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    margin-top: 20px;
    a {
        color: ${giphyWhite};
        opacity: 0.75;
        &:hover {
            opacity: 1;
            text-decoration: none;
        }
    }
`

const HelpCenterLink = ({ id, text }: { id: number; text: string }) => (
    <a href={`https://support.giphy.com/hc/en-us/articles/${id}`} target="_blank" rel="noreferrer">
        {text}
    </a>
)

type Props = {
    type: 'gifs' | 'stickers'
    searchResponseId: string
    clipSearchResponseId: string
    searchTerm: string
    onClose: Function
}

const ReportSearch = ({ type, searchResponseId, clipSearchResponseId, searchTerm, onClose = () => {} }: Props) => {
    const { sendMessage } = useContext(MessageContext)
    const [selectedReasons, setSelectedReasons] = useState<string[]>([])
    const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>([])
    const [additionalInfo, setAdditionalInfo] = useState('')

    const submitClicked = () => {
        for (const contentType of selectedContentTypes) {
            const responseId = contentType === 'clips' ? clipSearchResponseId : searchResponseId
            reportSearch(selectedReasons.join(','), additionalInfo, responseId, searchTerm, contentType)
        }
        sendMessage(`Successfully submitted report!`)
        onClose()
    }

    const closeClicked = () => {
        onClose()
    }

    return (
        <>
            <Dim onClick={closeClicked} />
            <Modal>
                <ColorBar />
                <CloseButton onClick={closeClicked} />
                <Heading>
                    Why are you reporting the search for &quot;<Term>{searchTerm}</Term>&quot;?
                </Heading>
                <ReasonsList>
                    {REASONS_ORDER.map((key) => (
                        <CheckboxContainer stacked={true} key={key}>
                            <StyledCheckbox
                                isChecked={selectedReasons.includes(key)}
                                onChange={(checked: boolean) =>
                                    setSelectedReasons((prev) => (checked ? concat(prev, key) : without(prev, key)))
                                }
                            />
                            {REASONS_DESCRIPTION[key]}
                        </CheckboxContainer>
                    ))}
                </ReasonsList>
                <Heading>
                    What type of content are you reporting?
                    <RequiredTooltip label={'This section must be completed before submitting'} maxWidth={205}>
                        <RequiredLabel>Required</RequiredLabel>
                    </RequiredTooltip>
                </Heading>
                <ContentType>
                    <CheckboxContainer stacked={false}>
                        <StyledCheckbox
                            isChecked={selectedContentTypes.includes(type)}
                            onChange={(checked: boolean) => {
                                setSelectedContentTypes((prev) => (checked ? concat(prev, type) : without(prev, type)))
                            }}
                        />
                        {type === 'stickers' ? 'Stickers' : 'GIFs'}
                    </CheckboxContainer>
                    <CheckboxContainer stacked={false}>
                        <StyledCheckbox
                            isChecked={selectedContentTypes.includes('clips')}
                            onChange={(checked: boolean) => {
                                setSelectedContentTypes((prev) =>
                                    checked ? concat(prev, 'clips') : without(prev, 'clips')
                                )
                            }}
                        />
                        Clips
                    </CheckboxContainer>
                </ContentType>
                <p>
                    Learn how to find the results you want and how these reports are used{' '}
                    <HelpCenterLink id={SEARCH_INFO} text={'here'} />.
                </p>
                <br />
                <Heading>
                    Any additional information?
                    <RequiredTooltip label={'This section must be completed before submitting'} maxWidth={205}>
                        <RequiredLabel>Required</RequiredLabel>
                    </RequiredTooltip>
                </Heading>
                <AdditionalInfoInput
                    initialValue={additionalInfo}
                    inputProps={{
                        as: 'textarea',
                        placeholder: '',
                        onChange: (evt) => setAdditionalInfo(evt.target.value),
                    }}
                />
                <Actions>
                    <SubmitButton onClick={submitClicked} disabled={!selectedContentTypes.length || !additionalInfo}>
                        Submit
                    </SubmitButton>
                    <CancelButton onClick={closeClicked}>Cancel</CancelButton>
                </Actions>
                <Footer>
                    <HelpCenterLink id={REPORT_GIF} text={'Report an Inappropriate GIF '} /> |{' '}
                    <HelpCenterLink id={DMCA} text={'DMCA Copyright Policy'} /> |
                    <HelpCenterLink id={GUIDELINES} text={'Community Guidelines '} />
                </Footer>
            </Modal>
        </>
    )
}

export default ReportSearch
